import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import { SalesReport } from "./pos.type";

// Date-range sales report. Both dates are inclusive (YYYY-MM-DD) and the range
// must be <= 365 days. A single-day report is just startDate === endDate.
interface SalesReportRange {
  restaurantId: string;
  startDate: string;
  endDate: string;
}

// A file download plus the server's own filename (the source of truth). The
// filename is null when Content-Disposition is absent or unreadable — e.g. the
// backend hasn't exposed it via Access-Control-Expose-Headers on a cross-origin
// call — in which case callers should fall back to a locally built name.
export interface ReportDownload {
  blob: Blob;
  filename: string | null;
}

function parseContentDispositionFilename(response: Response): string | null {
  const header = response.headers.get("Content-Disposition");
  if (!header) return null;
  // Prefer RFC 5987 `filename*=UTF-8''...` over the plain `filename="..."`.
  const encoded = header.match(/filename\*=(?:UTF-8'')?"?([^";]+)"?/i);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      return encoded[1];
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1] : null;
}

export const SalesReportApi = createApi({
  reducerPath: "salesReportApi",
  tagTypes: ["salesReport"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    getSalesReport: build.query<SalesReport, SalesReportRange>({
      query: ({ restaurantId, startDate, endDate }) => ({
        url: `restaurants/${restaurantId}/sales_report`,
        method: "GET",
        params: { start_date: startDate, end_date: endDate },
      }),
      providesTags: ["salesReport"],
    }),

    getSalesReportPdf: build.query<Blob, SalesReportRange>({
      query: ({ restaurantId, startDate, endDate }) => ({
        url: `restaurants/${restaurantId}/sales_report`,
        method: "GET",
        params: { start_date: startDate, end_date: endDate, format: "pdf" },
        responseHandler: async (response: Response) => {
          return await response.blob();
        },
      }),
      providesTags: ["salesReport"],
    }),

    getSalesReportCsv: build.query<ReportDownload, SalesReportRange>({
      query: ({ restaurantId, startDate, endDate }) => ({
        url: `restaurants/${restaurantId}/sales_report`,
        method: "GET",
        params: { start_date: startDate, end_date: endDate, format: "csv" },
        responseHandler: async (response: Response) => ({
          blob: await response.blob(),
          filename: parseContentDispositionFilename(response),
        }),
      }),
      providesTags: ["salesReport"],
    }),
  }),
});

export const {
  useGetSalesReportQuery,
  useGetSalesReportPdfQuery,
  useLazyGetSalesReportPdfQuery,
  useLazyGetSalesReportCsvQuery,
} = SalesReportApi;
